import React from 'react';
import { Package } from 'lucide-react';
import { ProductsToolbar } from '../../ProductsToolbar';
import { ProductSkeleton } from '../../ProductSkeleton';
import { ProductCard } from '../../ProductCard';
import { EmptyState } from '../../ui/EmptyState';
import { Button } from '../../ui/Button';
import { useVocab, useIsAppointments } from '../../../hooks/useVocab';

/**
 * Products grid with toolbar, error/empty/loading states, and infinite-scroll
 * sentinel. All state is owned by the parent; this is purely presentational.
 */
export function StoreProductsTab({
    products,
    visibleProducts,
    filteredProducts,
    productsLoading,
    productsError,
    categoriesError,
    categoriesLoading,
    categories,
    categoryMap,
    selectedCategory,
    searchTerm,
    sortBy,
    onSearchChange,
    onSortChange,
    onCategorySelect,
    onClearFilters,
    onRefetchProducts,
    onAddToCart,
    onRemoveFromCart,
    onSelectProduct,
    onRequestPaidOptions,
    cart,
    hasMore,
    onLoadMore,
    sentinelRef,
    rtl,
}) {
    const t = useVocab();
    const isAppt = useIsAppointments();
    // Distinguish "catalog is genuinely empty" from "filters hid everything".
    const catalogEmpty = !products || products.length === 0;
    const filterActive = !!(searchTerm || selectedCategory);
    return (
        <div className="space-y-6 mb-28">
            <ProductsToolbar
                searchTerm={searchTerm}
                onSearchChange={onSearchChange}
                sortBy={sortBy}
                onSortChange={onSortChange}
                categories={categories}
                selectedCategory={selectedCategory}
                onCategorySelect={onCategorySelect}
                onClearFilters={onClearFilters}
                loadingCategories={categoriesLoading}
                rtl={rtl}
            />

            {(productsError || categoriesError) && !productsLoading && (
                <div className="bg-danger-50 border border-danger-100 rounded-xl p-6 text-center space-y-3 animate-fade-in">
                    <h3 className="heading-sm text-danger-700">בעיה בטעינת מוצרים</h3>
                    <p className="text-danger-700/80 text-sm">{productsError || categoriesError}</p>
                    <Button variant="danger" onClick={onRefetchProducts}>נסה שוב</Button>
                </div>
            )}

            {productsLoading && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                    {Array.from({ length: 12 }).map((_, i) => <ProductSkeleton key={i} />)}
                </div>
            )}

            {!productsLoading && filteredProducts.length === 0 && (
                <div className="surface-soft rounded-2xl py-16 px-6 animate-fade-in">
                    <EmptyState
                        icon={<Package />}
                        title={catalogEmpty && !filterActive
                            ? (isAppt ? 'עוד אין שירותים לקביעה' : 'עוד אין מוצרים בחנות')
                            : `לא נמצאו ${t.items}`}
                        description={catalogEmpty && !filterActive
                            ? (isAppt
                                ? 'כדי שמטופלים יוכלו לקבוע תור, צריך קודם להוסיף שירות אחד לפחות. שעות הקבלה (משבצות הזמן) קובעות רק מתי אפשר לקבוע — לא מה.'
                                : 'עדיין לא נוספו מוצרים לחנות.')
                            : `נסו לשנות את החיפוש או לאפס סינונים כדי לראות ${t.items} נוספים.`}
                        action={filterActive && (
                            <Button variant="primary" onClick={onClearFilters}>נקה סינונים</Button>
                        )}
                    />
                </div>
            )}

            {!productsLoading && filteredProducts.length > 0 && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
                        {visibleProducts.map(product => {
                            const cartItem = cart.find(item => item.product_id === product.product_id);
                            const quantity = cartItem ? cartItem.quantity : 0;
                            const categoryName = product.category_id ? categoryMap[product.category_id] : null;
                            return (
                                <ProductCard
                                    key={product.product_id}
                                    product={product}
                                    onAddToCart={onAddToCart}
                                    onRemoveFromCart={onRemoveFromCart}
                                    quantity={quantity}
                                    onDetails={onSelectProduct}
                                    onRequestPaidOptions={onRequestPaidOptions}
                                    rtl={rtl}
                                    categoryName={categoryName}
                                />
                            );
                        })}
                    </div>

                    <div className="flex flex-col items-center justify-center pt-8">
                        {hasMore && (
                            <>
                                <Button variant="outline" onClick={onLoadMore}>טען עוד</Button>
                                <div ref={sentinelRef} className="h-2 w-full" />
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default StoreProductsTab;
